import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLocations } from '@/Locations/hooks/use-locations';
import { useUserLocation } from '@/Locations/hooks/use-user-location';
import { useSiteInfo } from '@/SiteInfo/hooks/use-site-info';
import { getStoreStatus } from '@/Locations/lib/format-hours';
import { sendContactForm } from '@/Contact/api/contact';
import { SEO } from '@/components/SEO';
import { IconAlertCircle, IconCircleCheck, IconClock, IconLoader4, IconMail, IconMapPin, IconPhone } from '@tabler/icons-react';

interface FormState {
  name: string;
  email: string;
  phone: string;
  store: string;
  message: string;
  honeypot: string;
}

const Contact = () => {
  const { data: locations, isLoading: locationsLoading } = useLocations();
  const { selectedLocation } = useUserLocation();
  const { data: siteInfo } = useSiteInfo();

  const [formData, setFormData] = useState<FormState>({
    name: '',
    email: '',
    phone: '',
    store: '',
    message: '',
    honeypot: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Find the user's selected store details
  const userStore = locations?.find(loc => loc.LocationTitle === selectedLocation);

  const formatAddress = (loc: typeof userStore) => {
    if (!loc) return '';
    const addr = loc.LocationAddress;
    return `${addr.LocationAddressLineOne}${addr.LocationAddressLineTwo ? ', ' + addr.LocationAddressLineTwo : ''}, ${addr.LocationAddressCity}, ${addr.LocationAddressState} ${addr.LocationAddressZipCode}`;
  };

  const handleInputChange = (field: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    // Clear status on new input
    if (submitStatus !== 'idle') {
      setSubmitStatus('idle');
      setErrorMessage('');
    }
  };

  const handleStoreChange = (value: string) => {
    setFormData(prev => ({ ...prev, store: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setSubmitStatus('error');
      setErrorMessage('Please fill in all required fields.');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus('error');
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    // Get location-specific emails if a store is selected
    const selectedStore = locations?.find(loc => loc.LocationTitle === formData.store);
    const locationEmails = selectedStore?.LocationContacts?.map(c => c.LocationContactEmailAddress).filter(Boolean);

    try {
      await sendContactForm({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || undefined,
        store: formData.store || undefined,
        message: formData.message.trim(),
        honeypot: formData.honeypot,
        // Pass site context for the Lambda to use in email formatting
        siteName: siteInfo?.SiteTitle || 'Store',
        siteEmail: siteInfo?.SiteEmailAddress || '',
        siteLogo: siteInfo?.SiteInverseLogo?.url || siteInfo?.SiteLogo?.url,
        sitePrimaryColor: siteInfo?.SitePrimaryColor,
        // Location-specific emails (Lambda will use these first, fallback to siteEmail)
        locationEmails: locationEmails?.length ? locationEmails : undefined,
      });

      setSubmitStatus('success');
      // Reset form on success
      setFormData({
        name: '',
        email: '',
        phone: '',
        store: '',
        message: '',
        honeypot: '',
      });
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Failed to send message. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-24">
      <SEO
        title="Get in Touch with Us | Contact Support"
        description="Get in touch with us. We'd love to hear from you. Contact us for questions, feedback, or support."
      />
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Side - Contact Info */}
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-4">Your feedback matters—contact us anytime</h1>
              <p className="text-muted-foreground text-lg">
                Got a question or comment? We're here to help! At {siteInfo?.SiteTitle || 'our store'}, we believe in offering top-quality products and an exceptional shopping experience. Whether you want to learn more about our store brands, inquire about special offers, or have any other questions, don't hesitate to contact us. Your feedback is important, and we're always ready to assist you!
              </p>
            </div>

            {/* Selected Store Details - Only show if user has selected a store */}
            {userStore && (
              <Card className="border-0 shadow-none bg-muted/30">
                <CardContent className="p-6 space-y-4">
                  <h3 className="font-semibold text-lg">Your Store</h3>
                  <p className="text-sm text-muted-foreground font-medium">{userStore.LocationTitle}</p>

                  <div className="space-y-3">
                    <div className="flex items-start gap-3 text-muted-foreground">
                      <IconMapPin className="h-4 w-4 mt-1 shrink-0 text-primary" />
                      <span className="text-sm">{formatAddress(userStore)}</span>
                    </div>

                    <div className="flex items-start gap-3 text-muted-foreground">
                      <IconPhone className="h-4 w-4 mt-0.5 text-primary" />
                      <span className="text-sm">{userStore.LocationPhoneNumber}</span>
                    </div>

                    {userStore.LocationHours?.length > 0 && (
                      <div className="flex items-start gap-3 text-muted-foreground">
                        <IconClock className="h-4 w-4 mt-0.5 text-primary" />
                        <span className="text-sm">{getStoreStatus(userStore.LocationHours).message}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* General Contact */}
            {siteInfo?.SiteEmailAddress && (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">General Inquiries</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <IconMail className="h-4 w-4 shrink-0 text-primary" />
                    <a href={`mailto:${siteInfo.SiteEmailAddress}`} className="text-sm hover:underline">
                      {siteInfo.SiteEmailAddress}
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Side - Contact Form */}
          <Card className="border shadow-sm">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot field - hidden from users, catches bots */}
                <input
                  type="text"
                  name="website"
                  value={formData.honeypot}
                  onChange={handleInputChange('honeypot')}
                  className="absolute -left-[9999px] opacity-0"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />

                <div className="space-y-2">
                  <Label htmlFor="name">Name <span className="text-destructive">*</span></Label>
                  <Input
                    id="name"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={handleInputChange('name')}
                    disabled={isSubmitting}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email <span className="text-destructive">*</span></Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    disabled={isSubmitting}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone (optional)</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="(555) 555-5555"
                    value={formData.phone}
                    onChange={handleInputChange('phone')}
                    disabled={isSubmitting}
                  />
                </div>

                {/* Location Select */}
                {!locationsLoading && locations && locations.length > 0 && (
                  <div className="space-y-2">
                    <Label>Related Store</Label>
                    <Select
                      value={formData.store}
                      onValueChange={handleStoreChange}
                      disabled={isSubmitting}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a store (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {[...locations].sort((a, b) => a.LocationTitle.localeCompare(b.LocationTitle)).map((location) => (
                          <SelectItem key={location.LocationTitle} value={location.LocationTitle}>
                            {location.LocationTitle}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="message">Message <span className="text-destructive">*</span></Label>
                  <Textarea
                    id="message"
                    placeholder="How can we help you?"
                    rows={5}
                    className="resize-none"
                    value={formData.message}
                    onChange={handleInputChange('message')}
                    disabled={isSubmitting}
                    required
                  />
                </div>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <Alert className="bg-emerald-50 border-emerald-200">
                    <IconCircleCheck className="h-4 w-4 text-emerald-600" />
                    <AlertDescription className="text-emerald-800">
                      Thank you for your message! We'll get back to you soon.
                    </AlertDescription>
                  </Alert>
                )}

                {submitStatus === 'error' && (
                  <Alert variant="destructive">
                    <IconAlertCircle className="h-4 w-4" />
                    <AlertDescription>{errorMessage}</AlertDescription>
                  </Alert>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <IconLoader4 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  We typically respond within 1 business day.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Contact;
